import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BarChart3, Briefcase, Building2, CalendarDays, CheckCircle2,
  Globe, Inbox, Link2, Mail, Newspaper, Pin, Upload, User, Users, Video, XCircle,
} from 'lucide-react';
import { jobsApi, candidatesApi, teamApi } from '../../../shared/services/api';
import TeamOverview from '../components/TeamOverview';
import { useRefresh } from '../../../shared/contexts/RefreshContext';
import { useAuth } from '../../auth';
import { canCreateJob } from '../../../shared/utils/permissions';

const PLATFORM_META = {
  teams:        { label: 'Microsoft Teams', icon: Video, color: '#7b5ea7' },
  zoom:         { label: 'Zoom',            icon: Video, color: '#2D8CFF' },
  google_meet:  { label: 'Google Meet',     icon: Video, color: '#00ac47' },
  face_to_face: { label: 'In-Person',       icon: Users, color: '#e3b341' },
};

function parseSlotDate(slotStr) {
  if (!slotStr) return null;
  const d = new Date(slotStr);
  return isNaN(d.getTime()) ? null : d;
}

function formatTime(slotStr) {
  const d = parseSlotDate(slotStr);
  if (!d) return slotStr;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dateDayKey(date) {
  const p = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

function friendlyDayLabel(dateKey) {
  const today = dateDayKey(new Date());
  const tomorrow = dateDayKey(new Date(Date.now() + 86400000));
  if (dateKey === today) return 'TODAY';
  if (dateKey === tomorrow) return 'TOMORROW';
  const d = new Date(dateKey);
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' }).toUpperCase();
}

const SCORE_BAND_COLORS = {
  'Strong Match': 'var(--green)',
  'Good Match': 'var(--accent)',
  'Partial Match': 'var(--yellow)',
  'Not Recommended': 'var(--red)',
};

const SOURCE_COLORS = {
  'LinkedIn': '#0077b5',
  'Indeed': '#2164f3',
  'Naukri': '#ff7555',
  'Monster': '#6e45e2',
  'Career Portal': '#3fb950',
  'Upload': 'var(--text-muted)',
  'Email': 'var(--purple)',
};

const SOURCE_ICONS = {
  'LinkedIn': Link2,
  'Indeed': Briefcase,
  'Naukri': Newspaper,
  'Monster': Globe,
  'Career Portal': Building2,
  'Upload': Upload,
  'Email': Mail,
};

// Sources arrive as free-text, so anything unrecognised still needs a mark.
function SourceIcon({ source, size = 14 }) {
  const Icon = SOURCE_ICONS[source] || Pin;
  return <Icon size={size} strokeWidth={1.9} style={{ flexShrink: 0 }} />;
}

const REJECTED_STAGES = ['rejected_resume', 'rejected_interview', 'rejected_final'];

export default function Dashboard() {
  const { user } = useAuth();
  const mayCreate = canCreateJob(user);
  const { refreshKey } = useRefresh();
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState(null);
  const [allCandidates, setAllCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(''); // '' = All Jobs

  useEffect(() => {
    setLoading(true);
    // Archived candidates are fetched too. Everyone rejected at resume screening
    // is archived automatically, so leaving them out hid most rejections and
    // most of the people who ever applied.
    Promise.all([jobsApi.list(), candidatesApi.list(), candidatesApi.list({ archived: true })])
      .then(([j, c, archived]) => {
        setJobs(j);
        const seen = new Set();
        setAllCandidates([...c, ...archived].filter(x => !seen.has(x.id) && seen.add(x.id)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // Separate so a failure here never blanks the rest of the dashboard.
    teamApi.myTeam().then(setTeam).catch(() => setTeam(null));
  }, [refreshKey]); // eslint-disable-line

  // Filter candidates by selected job (or use all)
  const candidates = selectedJobId
    ? allCandidates.filter(c => c.job_id === selectedJobId)
    : allCandidates;

  const selectedJob = jobs.find(j => j.id === selectedJobId) || null;

  const activeJobs = jobs.filter(j => j.status === 'Active').length;
  // Deleted jobs are not counted, matching the job picker's "All Jobs (n)".
  const liveJobs = jobs.filter(j => j.status !== 'Deleted').length;
  const totalCandidates = candidates.length;
  // Resume-threshold bucket (tab_status). Candidates list Status is the current step.
  const shortlisted = candidates.filter(c => (c.tab_status || c.status) === 'shortlisted').length;
  const scored = candidates.filter(c => c.resume_score !== null && c.resume_score !== undefined);

  // How far each candidate got, following the pipeline on the candidate page.
  // The tiles and the funnel both read this, so they cannot disagree. The
  // funnel used to mix measures — "Passed Screening" was a score test while
  // "Reached Interview" was a stage — so a low scorer HR sent to interview made
  // it grow (3 then 5). A rejected candidate counts up to the round they were
  // rejected at.
  const STAGE_RANK = {
    applied: 0, resume_scored: 1,
    resume_reviewed: 2, interview_invited: 2, interview_canceled: 2,
    interview_scheduled: 3, interview_in_progress: 4, interview_completed: 4,
    second_round_invited: 5, second_round_scheduled: 5, second_round_completed: 6,
    completed: 7,
    rejected_resume: 1, rejected_interview: 3, rejected_final: 5,
  };
  const rankOf = (c) => {
    let rank = STAGE_RANK[c.pipeline_stage] ?? 0;
    if (c.resume_score !== null && c.resume_score !== undefined) rank = Math.max(rank, 1);
    if (c.interview_status === 'Completed') rank = Math.max(rank, 4);
    return rank;
  };
  const reached = (n) => candidates.filter(c => rankOf(c) >= n).length;
  const rejected = candidates.filter(c => REJECTED_STAGES.includes(c.pipeline_stage)).length;
  const interviewed = reached(4);
  // Set but not yet sat, and not rejected.
  const scheduled = candidates.filter(c => rankOf(c) === 3 && !REJECTED_STAGES.includes(c.pipeline_stage)).length;

  // Source breakdown
  const sourceMap = {};
  candidates.forEach(c => {
    const src = c.source || 'Upload';
    sourceMap[src] = (sourceMap[src] || 0) + 1;
  });
  const sources = Object.entries(sourceMap).sort((a, b) => b[1] - a[1]);

  // Score band breakdown
  const bandMap = {};
  candidates.forEach(c => {
    if (c.resume_score_band) {
      bandMap[c.resume_score_band] = (bandMap[c.resume_score_band] || 0) + 1;
    }
  });

  const recentJobs = selectedJobId
    ? jobs.filter(j => j.id === selectedJobId)
    : [...jobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

  const clearedJobScore = (c) => {
    const job = jobs.find(j => j.id === c.job_id);
    const resumeBar = job?.resume_score_threshold ?? 75;
    const interviewBar = job?.interview_score_threshold ?? 60;
    const resume = Number(c.resume_score);
    const interview = Number(c.interview_score);
    const average = Number(c.final_score);
    if (!Number.isFinite(resume) || !Number.isFinite(interview) || !Number.isFinite(average)) return false;
    return resume >= resumeBar && interview >= interviewBar && average >= 60;
  };
  const topCandidates = [...candidates]
    .filter(clearedJobScore)
    .sort((a, b) => b.final_score - a.final_score)
    .slice(0, 5);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Recruitment Dashboard</div>
          <div className="page-subtitle">
            {selectedJob
              ? `Showing data for: ${selectedJob.title}`
              : 'AI-powered hiring pipeline — all jobs'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Job filter */}
          <select
            value={selectedJobId}
            onChange={e => setSelectedJobId(e.target.value)}
            style={{
              padding: '8px 14px',
              background: 'var(--bg-surface)',
              border: `1px solid ${selectedJobId ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 8,
              color: selectedJobId ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: 13,
              fontWeight: selectedJobId ? 600 : 400,
              cursor: 'pointer',
              minWidth: 200,
              outline: 'none',
            }}
          >
            <option value="">All Jobs ({jobs.filter(j => j.status !== 'Deleted').length})</option>
            {[...jobs]
              .filter(j => j.status !== 'Deleted')
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map(j => (
                <option key={j.id} value={j.id}>
                  {j.title || 'Untitled'} {j.status === 'Active' ? '' : `(${j.status})`}
                </option>
              ))}
          </select>
          {selectedJobId && (
            <button
              onClick={() => setSelectedJobId('')}
              style={{
                padding: '8px 12px', borderRadius: 8, fontSize: 12,
                background: 'none', border: '1px solid var(--border)',
                color: 'var(--text-muted)', cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
          {mayCreate && <Link to="/jobs/new" className="btn btn-primary">+ New Job</Link>}
        </div>
      </div>

      <TeamOverview team={team} jobs={jobs} candidates={allCandidates} />

      {/* Selected job context bar */}
      {selectedJob && (
        <div style={{
          background: 'rgba(37,99,235,0.06)',
          border: '1px solid rgba(37,99,235,0.2)',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 20,
          display: 'flex',
          gap: 24,
          fontSize: 13,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Job: </span>
            <strong style={{ color: 'var(--accent)' }}>{selectedJob.title}</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Status: </span>
            <StatusBadge status={selectedJob.status} />
          </span>
          {selectedJob.department && (
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Dept: </span>
              <strong>{selectedJob.department}</strong>
            </span>
          )}
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Resume threshold: </span>
            <strong style={{ color: 'var(--green)' }}>{selectedJob.resume_score_threshold ?? 75}</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Score weights: </span>
            <strong style={{ color: 'var(--accent)' }}>
              {Math.round((selectedJob.resume_weight ?? 0.4) * 100)}% resume
            </strong>
            {' / '}
            <strong style={{ color: 'var(--purple)' }}>
              {Math.round((selectedJob.interview_weight ?? 0.6) * 100)}% interview
            </strong>
          </span>
          <Link to={`/jobs/${selectedJob.id}`} className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}>
            Open Job <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
      )}

      {/* Top Stats */}
      <div className="stats-grid">
        {[
          selectedJobId
            ? { label: 'Job Status', value: selectedJob?.status || '—', sub: selectedJob?.employment_type || '', color: 'var(--accent)', mono: false }
            : { label: 'Active Jobs', value: activeJobs, sub: `${liveJobs} total`, color: 'var(--accent)' },
          { label: 'Total Candidates', value: totalCandidates, sub: selectedJobId ? 'for this job' : 'across all jobs', color: 'var(--purple)' },
          { label: 'Interviews Done', value: interviewed, sub: `${scheduled} scheduled`, color: '#d29922' },
          { label: 'Shortlisted', value: shortlisted, sub: 'resume score meets the bar', color: 'var(--green)' },
        ].map(stat => (
          <div className="stat-card" key={stat.label}>
            <div className="stat-label">{stat.label}</div>
            <div className="stat-value" style={{ color: stat.color, fontSize: typeof stat.value === 'string' && stat.value.length > 4 ? 18 : undefined }}>
              {stat.value}
            </div>
            <div className="stat-change">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Pipeline stages — counts only. The per-stage conversion percentages
          were removed: their denominator shifted from stage to stage, and the
          last one divided by a figure that already contained its own numerator,
          so it read 100% whenever nothing was scheduled. */}
      {(() => {

        const stages = [
          { label: 'Applied',           value: totalCandidates, color: '#bc8cff', icon: User },
          { label: 'Resume Scored',     value: reached(1),      color: '#ffa657', icon: BarChart3 },
          { label: 'Reviewed',          value: reached(2),      color: '#58a6ff', icon: CheckCircle2 },
          { label: 'AI Interview Set',  value: reached(3),      color: '#d29922', icon: CalendarDays },
          { label: 'AI Interview Done', value: reached(4),      color: '#3fb950', icon: Video },
          { label: '2nd Round',         value: reached(5),      color: '#ea580c', icon: CalendarDays },
          { label: 'Selected',          value: reached(7),      color: '#3fb950', icon: CheckCircle2 },
          // Rejected is an outcome, not a narrowing of the one before it: a
          // candidate can be rejected at any stage, so it sits outside the chain.
          { label: 'Rejected',          value: rejected,        color: '#f85149', icon: XCircle },
        ];

        return (
          <div className="card mt-6" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div className="card-title">Candidate Conversion Funnel</div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {selectedJobId ? selectedJob?.title : 'drop-off at each stage'}
              </span>
            </div>
            <div className="r-funnel" style={{ display: 'flex', alignItems: 'stretch', overflow: 'hidden', borderRadius: 8, border: '1px solid var(--border)' }}>
              {stages.map((stage, i) => {
                return (
                  <React.Fragment key={stage.label}>
                    <div className="r-funnel-stage" style={{
                      flex: 1, padding: '14px 10px', background: 'var(--bg-hover)', textAlign: 'center',
                    }}>
                      <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}>
                        <stage.icon size={18} strokeWidth={1.9} style={{ color: stage.color }} />
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: stage.color, fontFamily: 'var(--font-mono)' }}>
                        {stage.value}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 3 }}>{stage.label}</div>
                    </div>
                    {i < stages.length - 1 && (
                      <div className="r-funnel-sep" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', fontSize: 16, padding: '0 2px', background: 'var(--bg-hover)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>›</div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="grid-2" style={{ marginBottom: 20 }}>
        {/* Source Breakdown */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Candidate Sources</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>where applicants come from</span>
          </div>
          {sources.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px 0' }}>
              <div className="empty-state-icon"><Inbox size={24} strokeWidth={1.6} /></div>
              <div className="empty-state-text">No candidates yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sources.map(([src, count]) => {
                const pct = totalCandidates > 0 ? Math.round((count / totalCandidates) * 100) : 0;
                const color = SOURCE_COLORS[src] || 'var(--text-muted)';
                return (
                  <div key={src}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SourceIcon source={src} />
                        <span style={{ fontWeight: 500 }}>{src}</span>
                      </span>
                      <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color }}>
                        {count}
                      </span>
                    </div>
                    {/* The bar still shows each source's share of all applicants,
                        which is a real whole — only the printed figure is gone. */}
                    <div style={{ height: 6, background: 'var(--bg-hover)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Score Band Distribution */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Score Band Distribution</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>resume AI scoring</span>
          </div>
          {Object.keys(bandMap).length === 0 ? (
            <div className="empty-state" style={{ padding: '20px 0' }}>
              <div className="empty-state-icon"><BarChart3 size={24} strokeWidth={1.6} /></div>
              <div className="empty-state-text">No scored candidates yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Strong Match', 'Good Match', 'Partial Match', 'Not Recommended'].map(band => {
                const count = bandMap[band] || 0;
                const pct = scored.length > 0 ? Math.round((count / scored.length) * 100) : 0;
                const color = SCORE_BAND_COLORS[band];
                return (
                  <div key={band}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color }}>{band}</span>
                      <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color }}>
                        {count} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({pct}%)</span>
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bg-hover)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, opacity: 0.8, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                {scored.length} of {totalCandidates} candidates scored · {totalCandidates - scored.length} pending
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid-2">
        {/* Recent Jobs */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Job Posts</div>
            <Link to="/jobs" className="btn btn-ghost btn-sm">View All <ArrowRight size={13} strokeWidth={2} /></Link>
          </div>
          {recentJobs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Briefcase size={40} strokeWidth={1.5} /></div>
              <div className="empty-state-title">No jobs yet</div>
              <div className="empty-state-text">Create your first job post to get started</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recentJobs.map(job => {
                const jobCands = candidates.filter(c => c.job_id === job.id);
                return (
                  <Link key={job.id} to={`/jobs/${job.id}`} style={{ textDecoration: 'none' }}>
                    <div style={{
                      padding: '12px 14px',
                      background: 'var(--bg-hover)',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{job.title}</div>
                        {job.job_code && (
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 2 }}>
                            Job Code: <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{job.job_code}</span>
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {job.department || 'No dept'} · {job.employment_type} · {jobCands.length} applicants
                        </div>
                      </div>
                      <StatusBadge status={job.status} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Candidates */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Top Candidates</div>
            <Link to="/candidates" className="btn btn-ghost btn-sm">View All <ArrowRight size={13} strokeWidth={2} /></Link>
          </div>
          {topCandidates.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Users size={40} strokeWidth={1.5} /></div>
              <div className="empty-state-title">No one has cleared the job score</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topCandidates.map((c, i) => (
                <Link key={c.id} to={`/candidates/${c.id}`} style={{ textDecoration: 'none' }}>
                  <div style={{
                    padding: '10px 14px',
                    background: 'var(--bg-hover)',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: i === 0 ? 'rgba(63,185,80,0.15)' : 'var(--bg-card)',
                        border: `1px solid ${i === 0 ? 'var(--green)' : 'var(--border-light)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                        color: i === 0 ? 'var(--green)' : 'var(--text-muted)'
                      }}>{i + 1}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{c.name || 'Unknown'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          {c.current_role || '—'}
                          {c.source && (
                            <span style={{ marginLeft: 6, opacity: 0.7, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              · <SourceIcon source={c.source} size={12} /> {c.source}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: c.final_score >= 70 ? 'var(--green)' : c.final_score >= 50 ? 'var(--accent)' : 'var(--yellow)',
                        fontFamily: 'var(--font-mono)'
                      }}>{c.final_score}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>final score</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active: 'badge-green',
    Draft: 'badge-gray',
    Paused: 'badge-yellow',
    Closed: 'badge-red',
  };
  return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
}
